/* ═══════════════════════════════════════════════════════════════════════════
   🌍 worldMapApi.js — the browser's side of the world map. The phone's twin is
   services/worldMapApi.ts; the rules both use are services/worldMap.js
   (generated from the app's worldMap.ts), and the server is mapRoutes.js.

   🟥 THE MAP NEVER READS THE BROWSER'S LOCATION. My place is the current city
      on my profile, or another city I choose on the map — the server turns the
      city into a position.
   ═══════════════════════════════════════════════════════════════════════════ */
import { postJson } from './vibeSocial';

const K_CELLS = 'allo_map_cells_v1';
const K_LOOK = 'allo_map_look_v1';
const store = {
  get(k) { try { return JSON.parse(window.localStorage.getItem(k) || 'null'); } catch { return null; } },
  set(k, v) { try { window.localStorage.setItem(k, JSON.stringify(v)); } catch { /* private mode */ } },
};

export const fetchMe = () => postJson('/api/map/me', {});
export const fetchNear = (prefixes) => postJson('/api/map/near', { prefixes });
export const fetchFriendsOnMap = () => postJson('/api/map/friends', {});
export const fetchWho = (id, kind) => postJson('/api/map/who', { id, kind });

/* ── my pages on the map ── */
/** Use the map as one of my pages (null: as me again) — I appear as it. Answers with /me's shape. */
export const setAsPage = (pageId) => postJson('/api/map/as-page', { pageId });
/** The map as my page sees it: its followers by region (counts only) and the pages of its kind. */
export const fetchPageView = (pageId) => postJson('/api/map/page-view', { pageId });

/* ── my place: a city ── */
export const searchCities = (q, lang) => postJson('/api/map/places', { q, lang });
/** Put me in a city I chose on the map (it stays there whatever my profile says). */
export const chooseCity = (placeId) => postJson('/api/map/presence', { placeId });
/** Put me back in my profile's current city. */
export const placeFromProfileCity = () => postJson('/api/map/presence', { fromProfile: true });

/** After a setting changes: the server re-reads it and moves, hides, names or lights the dot.
 *  🟥 Its answer is returned — a setting the server did not take must not look taken. */
export async function refreshAfterSetting() {
  return postJson('/api/map/presence', { refresh: true });
}

const toCells = (j) => {
  if (!j || !Array.isArray(j.cells)) return null;
  return {
    v: Number(j.v) || 0, people: Number(j.people) || 0, pages: Number(j.pages) || 0, online: Number(j.online) || 0,
    cells: j.cells.filter((c) => Array.isArray(c) && typeof c[0] === 'string').map((c) => ({ gh: c[0], n: Number(c[1]) || 0, p: Number(c[2]) || 0, o: Number(c[3]) || 0, ...(Number.isFinite(c[4]) && Number.isFinite(c[5]) ? { lat: c[4], lng: c[5] } : {}) })),
  };
};
export const cachedCells = () => toCells(store.get(K_CELLS));
export async function loadCells(cellsUrl) {
  let raw = null;
  if (cellsUrl && /^https:\/\//.test(cellsUrl)) {
    try { const r = await fetch(cellsUrl); if (r.ok) raw = await r.json(); } catch { raw = null; }
  }
  if (!raw) { const r = await postJson('/api/map/cells', {}); raw = r && r.ok ? r : null; }
  const c = toCells(raw);
  if (c) store.set(K_CELLS, { v: c.v, people: c.people, pages: c.pages, online: c.online, cells: raw.cells });
  return c;
}

const wx = new Map();
export async function fetchWeather(lat, lng) {
  const key = `${lat.toFixed(1)},${lng.toFixed(1)}`;
  const hit = wx.get(key);
  if (hit && Date.now() - hit.at < 20 * 60 * 1000) return hit.data;
  const r = await postJson('/api/map/weather', { lat, lng });
  if (!r || !r.ok) return null;
  wx.set(key, { at: Date.now(), data: r });
  return r;
}

export const rememberedLook = () => store.get(K_LOOK) || {};
export const rememberLook = (patch) => store.set(K_LOOK, { ...(store.get(K_LOOK) || {}), ...patch });
